/* global globalThis */
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Helmet} from "react-helmet";
import PropTypes from "prop-types";
import {LAST_QUERY_USAGE_KEY} from "../patientProfile/PatientProfileHeader";
import {Badge, Button, Card, Col, Container, Dropdown, Form, Row} from "react-bootstrap";
import ExportSummary from "./ExportSummary";
import ListView from "./components/patientList";
import CustomPagination from "../common/CustomPaginaton";
import {cancelRequest, searchPatientMain} from "../../actions/patientSearch/patientSearchActions";
import {PatientSearchDTO} from "../../dtos/PatientSearchDTO";
import axios from "../../utils/apiClient";
import Modals from "../../components/modals";
import SpinnerLoading from "../common/components/spinner";
import {getCustomListName, getCustomWorklistName, getQuickListId} from "../../utils/util";
import _ from "lodash";
import {AppPropertiesUtil, SessionStorageUtil} from "../../utils/localStorageUtil";
import {QuickListContext} from "./QuickListContext";
import {CE_RESTORE_OBJ} from "../common/MainLayout";
import {FilterGroupNew} from "../../components/filters/filterGroupNew";
import WorklistQuickfilter from "./worklistQuickfilter2";
import {getErrorCode, TRACK_EVENTS, trackPendoEvent} from "../../utils/pendoTrack";

const isEmpty = require("is-empty");

export const PRINT_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/print";
export const PRINT_SYNC_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/print-sync";
export const EXPORT_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/export";
export const SUMMARY_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/export-summary";
export const CUSTOM_LIST_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/custom-filtered-list/custom-lists";
export const WORKLIST_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/custom-work-list/work-list";
const LAST_QUERY_SETTINGS_SUFFIX = `-QLQuerySettings`;
const RECENT_QUERY_SETTINGS_KEY = `ce-recent-query-settings-key`;
const SEARCH_DELAY_MS = 600;
const MIN_SEARCH_LENGTH = 3;
const LONG_LOAD_THRESHOLD_MS = 1500;

const getTimestamp = () => (typeof performance === "undefined" ? Date.now() : performance.now());

const stringifyPatientKey = (patient) => {
  const seen = new WeakSet();
  return JSON.stringify(patient, (key, value) => {
    if (typeof value !== "object" || value === null) {
      return value;
    }
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);
    return value;
  });
};

const pathName = globalThis.location.pathname;
const modifiedPathName = pathName.endsWith("/") ? pathName.substring(0, pathName.length - 1) : pathName;
export const RETURN_TO_MY_PATIENTS_URL = encodeURIComponent(
  "returnTo_" + modifiedPathName + "?loadLastQuerySettings=true"
);

const componentName = "QuickList";

const VIEW = { card: "CARD", list: "LIST" };

const DEFAULT_CARD_VIEW_ACTION_OBJ = {
  fetchData: searchPatientMain,
  copyMap: {
    list: "patientCardDtos",
    totalSize: "totalSize",
    nowTotalSize: "nowTotalSize",
    soonTotalSize: "soonTotalSize",
    priorityTotalSize: "priorityTotalSize",
    pageSize: "pageSize",
    pageNumber: "pageNumber",
  },
};

const QuickLists = ({
  titleProp,
  filters,
  pageNumberProp,
  pageSizeProp,
  totalSizeProp,
  defaultFilterParamsProp,
  defaultSortParamsProp,
  exportPatientsUrl,
  quickListType,
  cardViewActionObj = DEFAULT_CARD_VIEW_ACTION_OBJ,
  listViewActionObj,
  startingView = VIEW.card,
  careManagementActions,
}) => {
  const qlsSettings = useContext(QuickListContext);
  const quicklistId = getQuickListId();
  const customWorklistName = getCustomWorklistName();
  const customListName = getCustomListName();

  const [returnToMyPatientsUrl, setReturnToMyPatientsUrl] = useState(RETURN_TO_MY_PATIENTS_URL);

  useEffect(() => {
    const location = globalThis.location.pathname;
    const workingPathName = location.endsWith("/") ? location.substring(0, location.length - 1) : location;
    if (quicklistId && !Number.isNaN(Number(quicklistId))) {
      setReturnToMyPatientsUrl(
        encodeURIComponent(
          "returnTo_" +
            workingPathName +
            encodeURIComponent("?loadLastQuerySettings=true&id=" + quicklistId + "&customListName=" + customListName)
        )
      );
    } else {
      setReturnToMyPatientsUrl(
        encodeURIComponent("returnTo_" + workingPathName + "?loadLastQuerySettings=true")
      );
    }
  }, [quickListType, quicklistId, customListName]);

  const [selectedPatients, setSelectedPatients] = useState(new Map());
  const [title, setTitle] = useState(titleProp);
  const [patientsList, setPatientsList] = useState({ default: [] });
  const [cardViewData, setCardViewData] = useState({});
  const [pageNumber, setPageNumber] = useState(pageNumberProp);
  const [pageSize, setPageSize] = useState(pageSizeProp);
  const [basePageSize, setBasePageSize] = useState(pageSizeProp);
  const [totalSize, setTotalSize] = useState(totalSizeProp);
  const filterApplied = true;
  const multipleFilterApplied = false;
  const [initialLoad, setInitialLoad] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [listViewLoadingData, setListViewLoadingData] = useState(!!listViewActionObj);

  const [defaultFilterParams, setDefaultFilterParams] = useState(defaultFilterParamsProp);
  const [filterParams, setFilterParams] = useState(defaultFilterParamsProp);
  const [sortParams, setSortParams] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);
  const [modalSize, setModalSize] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);
  const [showPrintSpinner, setShowPrintSpinner] = useState(false);
  const [showExportSpinner, setShowExportSpinner] = useState(false);
  const [view, setView] = useState(startingView);
  const [allPatientsChecked, setAllPatientsChecked] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const [activeFilterView, setActiveFilterView] = useState("RECOMMENDED_NOW");
  const [nowTotalSize, setNowTotalSize] = useState(0);
  const [soonTotalSize, setSoonTotalSize] = useState(0);
  const [priorityTotalSize, setPriorityTotalSize] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeoutRef = useRef(null);
  const selectedFilterRef = useRef([]);
  const previousViewRef = useRef(null);
  const lastSortSignatureRef = useRef("");
  const previousSearchLengthRef = useRef(0);
  const serverTotalSizeRef = useRef(null);
  const serverNowTotalSizeRef = useRef(null);
  const serverSoonTotalSizeRef = useRef(null);
  const serverPriorityTotalSizeRef = useRef(null);
  const getEffectiveSearchTerm = (term) => {
    const raw = term ?? searchTerm ?? "";
    return raw.trim().length >= MIN_SEARCH_LENGTH ? raw : "";
  };

  const applySearchTermToDto = (dto, term, pageSizeOverride) => {
    dto.searchTerm = getEffectiveSearchTerm(term);
    if (pageSizeOverride !== undefined && pageSizeOverride !== null) {
      dto.pageSize = pageSizeOverride;
    }
    return dto;
  };

  const normalizeTotal = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : 0;
  };

  const getActiveTotalSize = () => {
    if (activeFilterView === "RECOMMENDED_NOW") return normalizeTotal(nowTotalSize);
    if (activeFilterView === "RECOMMENDED_SOON") return normalizeTotal(soonTotalSize);
    if (activeFilterView === "PRIORITY_SCREENING") return normalizeTotal(priorityTotalSize);
    const normalizedTotal = normalizeTotal(totalSize);
    return normalizedTotal || normalizeTotal(pageSize);
  };

  const getServerActiveTotalSize = () => {
    if (activeFilterView === "RECOMMENDED_NOW") return normalizeTotal(serverNowTotalSizeRef.current);
    if (activeFilterView === "RECOMMENDED_SOON") return normalizeTotal(serverSoonTotalSizeRef.current);
    if (activeFilterView === "PRIORITY_SCREENING") return normalizeTotal(serverPriorityTotalSizeRef.current);
    return normalizeTotal(serverTotalSizeRef.current);
  };

  const getSearchAwarePageSize = (term, fallbackSize, allowFullSearchResults = false) => {
    const trimmed = (term ?? searchTerm ?? "").trim();
    if (trimmed.length >= MIN_SEARCH_LENGTH && allowFullSearchResults) {
      return getServerActiveTotalSize() || fallbackSize;
    }
    return fallbackSize;
  };

  const getLoadBucket = (durationMs) => {
    if (durationMs < 1000) return "under_1s";
    if (durationMs < 2000) return "1s_to_2s";
    if (durationMs < 3000) return "2s_to_3s";
    if (durationMs < 5000) return "3s_to_5s";
    return "5s_plus";
  };

  const getSortMetadata = (candidateSortParams) => {
    const normalizedSorts = Array.isArray(candidateSortParams) ? candidateSortParams : [];
    const signature = JSON.stringify(normalizedSorts);
    const primarySort = normalizedSorts[0] || {};
    const sortKey =
      primarySort?.paramName ||
      primarySort?.fieldName ||
      primarySort?.key ||
      primarySort?.name ||
      null;
    let sortDir = primarySort?.direction || primarySort?.sortOrder || primarySort?.order || null;
    if (!sortDir && typeof primarySort?.desc === "boolean") {
      sortDir = primarySort.desc ? "DESC" : "ASC";
    }
    return {
      signature,
      sortKey,
      sortDir: sortDir ? String(sortDir).toUpperCase() : null,
    };
  };

  const LAST_QUERY_SETTINGS_KEY = `${quickListType}` + LAST_QUERY_SETTINGS_SUFFIX;
  const PRINT_THRESHOLD = AppPropertiesUtil.get("export.download.limit");
  const priorityScreeningsEnabled = AppPropertiesUtil.get("priority.screenings.enabled");

  const setDefaultFilterParamHelper = (fp) => {
    setDefaultFilterParams(defaultFilterParamsProp);
    setFilterParams(fp);
  };

  const handleFilterChange = (fp, p) => {
    cancelRequest(setCanceled);
    const normalizedFilters = Array.isArray(fp) ? fp : [];
    if (normalizedFilters.length === 0) {
      trackPendoEvent(TRACK_EVENTS.NAV_FILTER_CLEARED_ALL, {
        route: globalThis.location?.pathname,
        quicklist_type: quickListType,
        source_module: "WorkListQuickList.handleFilterChange"
      });
    } else {
      trackPendoEvent(TRACK_EVENTS.NAV_FILTER_APPLIED, {
        route: globalThis.location?.pathname,
        quicklist_type: quickListType,
        filter_name: normalizedFilters.map((filterParam) => filterParam?.paramName).filter(Boolean).join(","),
        source_module: "WorkListQuickList.handleFilterChange"
      });
    }
    setFilterParams(fp);
    setSelectedPatients(new Map());
    if (!p) {
      p = 1;
    }
    const effectiveSize = getSearchAwarePageSize(searchTerm, pageSize);
    const dto = applySearchTermToDto(
      new PatientSearchDTO(fp, sortParams, p, effectiveSize, quickListType, totalSize, nowTotalSize, soonTotalSize, priorityTotalSize),
      searchTerm,
      effectiveSize
    );
    query(dto, title, view);
  };

  const handleFilterPageSize = (e) => {
    const result = e.target.value.split("-");
    const selectedSize = Number(result[0]);
    setBasePageSize(selectedSize);
    const effectiveSize = getSearchAwarePageSize(searchTerm, selectedSize);
    setPageSize(selectedSize);
    const dto = applySearchTermToDto(
      new PatientSearchDTO(filterParams, sortParams, 1, effectiveSize, quickListType, totalSize, nowTotalSize, soonTotalSize, priorityTotalSize),
      searchTerm,
      effectiveSize
    );
    query(dto, title, view);
  };

  const handlePaginationChange = (pn) => {
    setPageNumber(pn);
    const dto = applySearchTermToDto(
      new PatientSearchDTO(filterParams, sortParams, pn, getSearchAwarePageSize(searchTerm, pageSize), quickListType, totalSize, nowTotalSize, soonTotalSize, priorityTotalSize),
      searchTerm,
      getSearchAwarePageSize(searchTerm, pageSize)
    );
    query(dto, title, view);
  };

  const copyToPatientListVariables = useCallback((data, inView) => {
    let copyMap = null;

    if (!inView) {
      inView = view;
    }

    if (inView === VIEW.card || (inView === VIEW.list && !listViewActionObj)) {
      copyMap = cardViewActionObj.copyMap;
    } else if (inView === VIEW.list && listViewActionObj) {
      copyMap = listViewActionObj.copyMap;
    }

    const defaultPatients = _.get(data, copyMap["list"]) || [];

    setPatientsList({ default: defaultPatients });
    const rawTotalSize = _.get(data, copyMap["totalSize"]);
    const rawNowTotalSize = _.get(data, copyMap["nowTotalSize"]);
    const rawSoonTotalSize = _.get(data, copyMap["soonTotalSize"]);
    const rawPriorityTotalSize = _.get(data, copyMap["priorityTotalSize"]);
    serverTotalSizeRef.current = rawTotalSize;
    serverNowTotalSizeRef.current = rawNowTotalSize;
    serverSoonTotalSizeRef.current = rawSoonTotalSize;
    serverPriorityTotalSizeRef.current = rawPriorityTotalSize;
    setTotalSize(rawTotalSize);
    setNowTotalSize(rawNowTotalSize);
    setSoonTotalSize(rawSoonTotalSize);
    setPriorityTotalSize(rawPriorityTotalSize);
    setPageSize(_.get(data, copyMap["pageSize"]));
    setPageNumber(_.get(data, copyMap["pageNumber"]));
  }, [cardViewActionObj.copyMap, listViewActionObj, view]);

  useEffect(() => {
    if (cardViewData && cardViewData.pageNumber === pageNumber) {
      copyToPatientListVariables(cardViewData.data);
      setLoadingData(false);
    }
  }, [cardViewData, copyToPatientListVariables, pageNumber]);

  useEffect(() => {
    if (SessionStorageUtil.getObj(LAST_QUERY_USAGE_KEY)) return;

    if (activeFilterView) {
      const dto = applySearchTermToDto(
        new PatientSearchDTO(
          filterParams,
          sortParams,
          pageNumber,
          getSearchAwarePageSize(searchTerm, pageSize),
          quickListType,
          totalSize,
          nowTotalSize,
          soonTotalSize,
          priorityTotalSize
        ),
        searchTerm,
        getSearchAwarePageSize(searchTerm, pageSize)
      );
      query(dto, title, view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterView]);

  const query = async (patientSearchDTO, updatedTitle, updatedView, overrideSearchTerm) => {
    setLoadingData(true);
    const requestStart = getTimestamp();
    const requestedPageNumber = patientSearchDTO.pageNumber;
    setPageNumber(requestedPageNumber);

    const resolvedSearchTerm = getEffectiveSearchTerm(overrideSearchTerm ?? searchTerm);
    patientSearchDTO.searchTerm = resolvedSearchTerm;
    if (resolvedSearchTerm && resolvedSearchTerm.trim().length > 0) {
      patientSearchDTO.pageSize = patientSearchDTO.pageSize || basePageSize || pageSize;
    }
    if (activeFilterView && activeFilterView !== "UNSELECT") {
      patientSearchDTO.quickFilterName = activeFilterView;
    }

    const sortMetadata = getSortMetadata(patientSearchDTO.sortParams);
    if (sortMetadata.signature !== lastSortSignatureRef.current) {
      lastSortSignatureRef.current = sortMetadata.signature;
      if (sortMetadata.signature !== "[]") {
        trackPendoEvent(TRACK_EVENTS.UI_SORT_CHANGED, {
          route: globalThis.location?.pathname,
          quicklist_type: quickListType,
          source_module: "WorkListQuickList.query",
          sort_key: sortMetadata.sortKey,
          sort_dir: sortMetadata.sortDir,
        });
      }
    }

    const trackLongLoadIfNeeded = () => {
      const now = getTimestamp();
      const durationMs = Math.max(0, Math.round(now - requestStart));
      if (durationMs >= LONG_LOAD_THRESHOLD_MS) {
        trackPendoEvent(TRACK_EVENTS.PERF_LONG_LOAD, {
          route: globalThis.location?.pathname,
          quicklist_type: quickListType,
          source_module: "WorkListQuickList.query",
          load_ms_bucket: getLoadBucket(durationMs),
        });
      }
    };

    SessionStorageUtil.setObj(RECENT_QUERY_SETTINGS_KEY, LAST_QUERY_SETTINGS_KEY);
    SessionStorageUtil.setObj(LAST_QUERY_SETTINGS_KEY, {
      patientSearchDTO,
      view: updatedView,
      title: updatedTitle,
    });

    searchPatientMain(patientSearchDTO)
      .then((res) => {
        if (res.data) {
          const pageObject = {
            pageNumber: requestedPageNumber,
            data: res.data,
          };
          setCardViewData(pageObject);
        }
        setCanceled(false);
        trackLongLoadIfNeeded();
      })
      .catch((err) => {
        if (err?.message !== "cancel by user") {
          setCanceled(false);
        }
        setLoadingData(false);
        trackLongLoadIfNeeded();
      });
  };

  const fetchData = async (fp, quicklistName, ttl, v) => {
    const effectiveSize = getSearchAwarePageSize(searchTerm, pageSize);
    const dto = applySearchTermToDto(new PatientSearchDTO(fp, sortParams, pageNumber, effectiveSize, quicklistName), searchTerm, effectiveSize);
    query(dto, ttl, v);
  };

  useEffect(() => {
    const restoreObject = SessionStorageUtil.getObj(CE_RESTORE_OBJ);
    if (restoreObject?.recentKey === LAST_QUERY_SETTINGS_KEY) {
      // nothing else to do here; initialLoad triggers the logic below
    }
    setInitialLoad(true);
  }, [LAST_QUERY_SETTINGS_KEY, quicklistId]);

  useEffect(() => {
    if (!initialLoad) return;
    setInitialLoad(false);

    const restoreObject = SessionStorageUtil.getObj(CE_RESTORE_OBJ);
    let quicklistObj = SessionStorageUtil.getObj(LAST_QUERY_SETTINGS_KEY);
    const useLastQuery = SessionStorageUtil.getObj(LAST_QUERY_USAGE_KEY);

    if (restoreObject?.recentKey === LAST_QUERY_SETTINGS_KEY) {
      quicklistObj = restoreObject.quickListObj;
      SessionStorageUtil.setObj(LAST_QUERY_SETTINGS_KEY, quicklistObj);
      SessionStorageUtil.setObj(CE_RESTORE_OBJ, null);
    }

    if ((restoreObject || useLastQuery) && quicklistObj?.patientSearchDTO) {
      const dto = quicklistObj.patientSearchDTO;

      if (defaultSortParamsProp) {
        dto.sortParams = defaultSortParamsProp;
      }

      setTitle(quicklistObj.title);
      setView(quicklistObj.view);
      setDefaultFilterParamHelper(dto.filterParams);
      setSortParams(dto.sortParams);
      setPageNumber(dto.pageNumber);
      setPageSize(dto.pageSize);
      setActiveFilterView(dto.quickFilterName ?? "RECOMMENDED_NOW");
      setSearchTerm(dto.searchTerm ?? "");

      query(dto, quicklistObj.title, quicklistObj.view, dto.searchTerm ?? "");

      SessionStorageUtil.setObj(LAST_QUERY_USAGE_KEY, null);
      return;
    }

    setTitle(titleProp);
    setDefaultFilterParamHelper(defaultFilterParamsProp);
    if (defaultSortParamsProp) {
      setSortParams(defaultSortParamsProp);
      const dto = applySearchTermToDto(
        new PatientSearchDTO(
          defaultFilterParamsProp,
          defaultSortParamsProp,
          pageNumber,
          getSearchAwarePageSize(searchTerm, pageSize),
          quickListType
        ),
        searchTerm,
        getSearchAwarePageSize(searchTerm, pageSize)
      );
      query(dto, titleProp, view);
    } else {
      fetchData(defaultFilterParamsProp, quickListType, titleProp, view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoad]);

  const reloadPatientView = () => {
    fetchData(filterParams, quickListType, titleProp, view);
  };

  const summary = () => {
    axios
      .get(SUMMARY_URL, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setShowPrintSpinner(false);
        setShowExportSpinner(false);
        setModalTitle("Export Summary");
        setModalSize("lg");
        setModalMessage(<ExportSummary exportSummary={response.data} />);
        trackPendoEvent(TRACK_EVENTS.EXPORT_SUMMARY_VIEWED, {
          route: globalThis.location?.pathname,
          quicklist_type: quickListType,
          source_module: "WorkListQuickList.summary",
        });
      })
      .catch((error) => {
        setShowPrintSpinner(false);
        setShowExportSpinner(false);
        setModalTitle("Error");
        setModalMessage(error.response.data.message);
        setLoadingData(false);
        trackPendoEvent(TRACK_EVENTS.API_SUBMIT_FAILED, {
          route: globalThis.location?.pathname,
          quicklist_type: quickListType,
          source_module: "WorkListQuickList.summary",
          operation: "export_summary",
          error_code: getErrorCode(error, "export_summary_failed"),
        });
      });
  };

  const normalizeIdValue = (value) => {
    if (value === undefined || value === null) return null;
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : null;
  };

  const getPatientBaseIdentifier = (patient) => {
    if (!patient) return null;
    return (
      normalizeIdValue(patient.id) ??
      normalizeIdValue(patient.patientId) ??
      normalizeIdValue(patient.patientID) ??
      normalizeIdValue(patient.memberId) ??
      null
    );
  };

  const getPatientVisitIdentifier = (patient) => {
    if (!patient) return null;
    return (
      normalizeIdValue(patient.worklistItemId) ??
      normalizeIdValue(patient.worklistItemID) ??
      normalizeIdValue(patient.patientGapId) ??
      normalizeIdValue(patient.patientGapID) ??
      normalizeIdValue(patient.careGapId) ??
      normalizeIdValue(patient.careGapID) ??
      normalizeIdValue(patient.visitGapId) ??
      normalizeIdValue(patient.visitGapID) ??
      normalizeIdValue(patient.visitId) ??
      normalizeIdValue(patient.visitID) ??
      null
    );
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!previousViewRef.current) {
      previousViewRef.current = view;
      return;
    }

    if (previousViewRef.current !== view) {
      trackPendoEvent(TRACK_EVENTS.UI_VIEW_MODE_CHANGED, {
        route: globalThis.location?.pathname,
        source_module: "WorkListQuickList",
        view_mode: String(view || "").toLowerCase(),
      });
      previousViewRef.current = view;
    }
  }, [view]);

  const buildSelectedItemIdentifiers = () => {
    const baseIdentifiers = new Set();
    const fallbackIdentifiers = new Set();

    selectedPatients.forEach((value) => {
      const baseId = getPatientBaseIdentifier(value);
      if (baseId) {
        baseIdentifiers.add(baseId);
        return;
      }

      const visitId = getPatientVisitIdentifier(value);
      if (visitId) {
        fallbackIdentifiers.add(visitId);
      }
    });

    return [...baseIdentifiers, ...fallbackIdentifiers];
  };

  const exportPatients = () => {
    setShowExportSpinner(true);
    const exportSize = getSearchAwarePageSize(searchTerm, pageSize, true);
    const filterData = applySearchTermToDto(
      new PatientSearchDTO(filterParams, sortParams, pageNumber, exportSize, quickListType),
      searchTerm,
      exportSize
    );
    if (activeFilterView && activeFilterView !== "UNSELECT") {
      filterData.quickFilterName = activeFilterView;
    }
    const criteria = {};
    criteria.patientRequest = filterData;
    criteria.selectedItems = buildSelectedItemIdentifiers();
    criteria.quickList = quickListType;
    trackPendoEvent(TRACK_EVENTS.QUICKLIST_EXPORT_CSV, {
      route: globalThis.location?.pathname,
      quicklist_type: quickListType,
      row_scope: criteria.selectedItems?.length ? "selected" : "filtered",
      source_module: "WorkListQuickList.exportPatients",
      success: true
    });
    axios
      .post(exportPatientsUrl, criteria, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        summary();
      })
      .catch((error) => {
        trackPendoEvent(TRACK_EVENTS.QUICKLIST_EXPORT_CSV, {
          route: globalThis.location?.pathname,
          quicklist_type: quickListType,
          row_scope: criteria.selectedItems?.length ? "selected" : "filtered",
          source_module: "WorkListQuickList.exportPatients",
          success: false,
          error_code: getErrorCode(error, "quicklist_export_csv_failed")
        });
        setShowExportSpinner(false);
        setModalTitle("Error");
        setModalMessage(error.response.data.message);
      });
  };

  const getPatientSelectionKey = useCallback((patient) => {
    if (!patient) return null;

    const instanceKey = normalizeIdValue(patient.selectionInstanceKey);
    if (instanceKey) {
      return instanceKey;
    }

    const baseId =
      normalizeIdValue(patient.id) ??
      normalizeIdValue(patient.patientId) ??
      normalizeIdValue(patient.patientID) ??
      normalizeIdValue(patient.memberId) ??
      null;

    const gapId =
      normalizeIdValue(patient.careGapId) ??
      normalizeIdValue(patient.careGapID) ??
      normalizeIdValue(patient.patientGapId) ??
      normalizeIdValue(patient.patientGapID) ??
      normalizeIdValue(patient.visitGapId) ??
      normalizeIdValue(patient.visitGapID) ??
      normalizeIdValue(patient.visitId) ??
      normalizeIdValue(patient.visitID) ??
      normalizeIdValue(patient.gapId) ??
      normalizeIdValue(patient.gapID) ??
      normalizeIdValue(patient.worklistItemId) ??
      normalizeIdValue(patient.worklistItemID) ??
      null;

    if (baseId && gapId) {
      return `${baseId}|${gapId}`;
    }

    if (baseId) {
      return baseId;
    }

    if (gapId) {
      return `gap|${gapId}`;
    }

    return stringifyPatientKey(patient);
  }, []);

  const getPatientSelectionBaseKey = useCallback((patient) => {
    if (!patient) return null;
    const patientCopy = { ...patient };
    delete patientCopy.selectionInstanceKey;
    return getPatientSelectionKey(patientCopy);
  }, [getPatientSelectionKey]);

  const applyPatientSelection = (selectedPatientsMap, patient, selected) => {
    const key = getPatientSelectionKey(patient);
    if (!key) return;

    const baseKey = getPatientSelectionBaseKey(patient);
    if (selected) {
      selectedPatientsMap.set(key, patient);
    } else {
      selectedPatientsMap.delete(key);
    }

    if (baseKey && baseKey !== key) {
      selectedPatientsMap.delete(baseKey);
    }
  };

  const selectPatient = (patient, selected) => {
    const selectedPatientsCopy = new Map(selectedPatients);
    applyPatientSelection(selectedPatientsCopy, patient, selected);
    setSelectedPatients(selectedPatientsCopy);
  };

  const selectPatients = (patients, selected) => {
    const selectedPatientsCopy = new Map(selectedPatients);
    patients.forEach((patient) => applyPatientSelection(selectedPatientsCopy, patient, selected));
    setSelectedPatients(selectedPatientsCopy);
  };

  const [selectAllLabel, setSelectAllLabel] = useState("Select All");
  const filteredPatientList = patientsList;
  const displayCount = getActiveTotalSize();
  const displayPageSize = Number(pageSize) > 0 ? Number(pageSize) : 1;

  const isPatientSelected = useCallback((patient) => {
    const key = getPatientSelectionKey(patient);
    if (!key) return false;
    if (selectedPatients.has(key)) {
      return true;
    }
    const baseKey = getPatientSelectionBaseKey(patient);
    return baseKey ? selectedPatients.has(baseKey) : false;
  }, [getPatientSelectionBaseKey, getPatientSelectionKey, selectedPatients]);

  useEffect(() => {
    const patients = patientsList.default || [];
    if (!patients.length) {
      setAllPatientsChecked(false);
      return;
    }
    const allSelected = patients.every((patient) => isPatientSelected(patient));
    setAllPatientsChecked(allSelected);
  }, [patientsList, selectedPatients, isPatientSelected]);

  const selectOrUnSelectAll = (selected) => {
    const selectedPatientsMap = new Map();
    setAllPatientsChecked(selected);

    if (selected) {
      (patientsList.default || []).forEach((x) => {
        const key = getPatientSelectionKey(x);
        if (!key) return;
        selectedPatientsMap.set(key, x);
      });
    }
    setSelectedPatients(selectedPatientsMap);

    if (selectAllLabel) {
      setSelectAllLabel(selectAllLabel === "Select All" ? "Deselect All" : "Select All");
    }
  };

  const closeEvidenceModal = () => {
    const dto = applySearchTermToDto(
      new PatientSearchDTO(
        filterParams,
        sortParams,
        pageNumber,
        getSearchAwarePageSize(searchTerm, pageSize),
        quickListType
      ),
      searchTerm,
      getSearchAwarePageSize(searchTerm, pageSize)
    );
    query(dto, title, view);
  };

  const updateParent = (updatedRef, sourceType) => {
    const newState = patientsList.default;
    if (sourceType && sourceType === "SubmitRecentVisit") {
      newState.forEach((pat, patI) => {
        if (pat.id === updatedRef.patientId) {
          newState[patI].nextVisit = updatedRef.nextVisit;
          newState[patI].recentVisitStatus = updatedRef.recentVisitStatus;
          newState[patI].nextVisitInfo = updatedRef.nextVisitInfoDesc;
          newState[patI].recentVisit = updatedRef.recentVisit;
          newState[patI].recentVisitInfo = updatedRef.recentVisitInfoDesc;
          const patientListSectioned = {
            default: [...newState],
          };
          patientListSectioned.default = [...newState];
          setPatientsList(patientListSectioned);
        }
      });
    } else if (updatedRef) {
      let patIndex = null;
      let refIndex = null;
      newState.forEach((pat, patI) => {
        if (pat.id === updatedRef.patient.id) {
          patIndex = patI;
          pat.referralDtoList.forEach((ref, refI) => {
            if (ref.id === updatedRef.referralId) {
              refIndex = refI;
            }
          });
        }
      });
      if (newState[patIndex].referralDtoList[refIndex].status !== updatedRef.status) {
        newState[patIndex].referralDtoList[refIndex].status = updatedRef.status;
        const patientListSectioned = { default: [] };
        patientListSectioned.default = [...newState];
        setPatientsList(patientListSectioned);
      }
    }
  };

  const convertNumber = (number) => {
    if (number !== null && !Number.isNaN(Number(number))) {
      return number.toLocaleString();
    } else {
      return "";
    }
  };
  const handleSearchChange = (value = "") => {
    const searchValue = value ?? "";
    setSearchTerm(searchValue);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const trimmed = searchValue.trim();
    const previousLength = previousSearchLengthRef.current;
    const shouldReset =
      trimmed.length === 0 || (trimmed.length < MIN_SEARCH_LENGTH && previousLength >= MIN_SEARCH_LENGTH);
    if (shouldReset) {
      setPatientsList({ default: [] });
      setLoadingData(true);
      if (listViewActionObj) {
        setListViewLoadingData(true);
      }
    }
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH && previousLength < MIN_SEARCH_LENGTH) {
      previousSearchLengthRef.current = trimmed.length;
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      const currentTrimmed = searchValue.trim();
      const currentLength = currentTrimmed.length;
      const shouldSearch = currentLength >= MIN_SEARCH_LENGTH;
      if (!shouldSearch && !shouldReset) {
        previousSearchLengthRef.current = currentLength;
        return;
      }
      const firstPage = 1;
      setPageNumber(firstPage);
      const effectiveSize = getSearchAwarePageSize(shouldSearch ? searchValue : "", basePageSize);
      setPageSize(basePageSize);
      const termToUse = shouldSearch ? searchValue : "";
      if (shouldSearch) {
        trackPendoEvent(TRACK_EVENTS.NAV_SEARCH_SUBMITTED, {
          route: globalThis.location?.pathname,
          quicklist_type: quickListType,
          search_scope: "quicklist",
          source_module: "WorkListQuickList.handleSearchChange"
        });
      }
      const dto = applySearchTermToDto(
        new PatientSearchDTO(
          filterParams,
          sortParams,
          firstPage,
          effectiveSize,
          quickListType,
          totalSize,
          nowTotalSize,
          soonTotalSize,
          priorityTotalSize
        ),
        termToUse,
        effectiveSize
      );
      query(dto, title, view, termToUse);
      previousSearchLengthRef.current = currentLength;
    }, SEARCH_DELAY_MS);
  };

  const getEmptyStateMessage = () => {
    if (searchTerm.trim().length >= MIN_SEARCH_LENGTH) {
      return "No patients found in this list";
    }
    if (canceled) {
      return "Loading...";
    }
    return "No Patients Found";
  };
  const emptyStateMessage = getEmptyStateMessage();

  return (
    <Container fluid className="py-3">
      <Helmet>
        <title>{title} | CareEmpower</title>
      </Helmet>
      <div>
        <Row>
          <Col>
            {!loadingData && !listViewLoadingData ? (
              <FilterGroupNew
                filters={filters}
                handleChange={handleFilterChange}
                totalSize={totalSize}
                defaultFilterParams={defaultFilterParams}
                filterParams={filterParams}
                sortParams={sortParams}
                pageNumber={pageNumber}
                pageSize={pageSize}
                quickListType={quickListType}
                setModalMessage={setModalMessage}
                setModalSize={setModalSize}
                setModalTitle={setModalTitle}
                customWorklistName={customWorklistName}
                additionalValidation={qlsSettings.additionalFilterValidation}
              />
            ) : (
              <Button variant="light" id="filter-button" className="border nowrap px-3 mb-2" disabled>
                <i className="fa-solid fa-bars-filter me-2"></i> Filter
              </Button>
            )}
          </Col>
          <Col md={2} className="text-end">
            {(loadingData || listViewLoadingData) && (
              <SpinnerLoading />
            )}
          </Col>
        </Row>
        {(!isEmpty(patientsList.default) || !isEmpty(activeFilterView) || searchTerm.trim().length >= MIN_SEARCH_LENGTH) && (
          <>
            <Row className="mb-3">
              <Col>
                <WorklistQuickfilter
                  activeFilterView={activeFilterView}
                  setActiveFilterView={setActiveFilterView}
                  setPageNumber={setPageNumber}
                  nowTotalSize={nowTotalSize}
                  soonTotalSize={soonTotalSize}
                  priorityTotalSize={priorityTotalSize}
                  priorityScreeningsEnabled={priorityScreeningsEnabled}
                  loading={loadingData || listViewLoadingData}
                />
              </Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col>
                <Form.Check
                  checked={allPatientsChecked}
                  onChange={(e) => selectOrUnSelectAll(e.target.checked)}
                  type="checkbox"
                  id={`${componentName}-PatientSelect`}
                  className="d-inline-block ms-3 me-2"
                />
                <Badge pill bg="light" id={`${componentName}-PatientCount`} className="border text-dark me-2">
                  {convertNumber(displayCount)} Patients
                </Badge>
                <Form.Control
                  type="search"
                  placeholder="Search patients by name or ID"
                  aria-label="Search patients within this list"
                  id={`${componentName}-PatientSearch`}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="d-inline-block ms-3"
                  style={{ maxWidth: "320px" }}
                />
                {(showExportSpinner || showPrintSpinner) && (
                  <SpinnerLoading />
                )}
              </Col>
              <Col className="d-flex justify-content-end align-items-center gap-3">
                <div>
                  <select
                    className="form-select"
                    id={`${componentName}-ResultsPerPage`}
                    disabled={!filterApplied}
                    defaultValue="20-Results"
                    onChange={(e) => handleFilterPageSize(e)}
                  >
                    <option value="20-Results" disabled hidden>
                      20 Results Per Page
                    </option>
                    <option value="20-Results">20 Results Per Page</option>
                    <option value="50-Results">50 Results Per Page</option>
                  </select>
                </div>
                <div>
                  <CustomPagination
                    paginateButClicked={handlePaginationChange}
                    MAX_PAGE={Math.ceil(displayCount / displayPageSize)}
                    pageNumber={Math.ceil(pageNumber)}
                  />
                </div>

                {exportPatientsUrl != null && (
                  <Dropdown>
                    <Dropdown.Toggle id={`${componentName}-Export`} variant="link" className="border-0">
                      <i className="fa-solid fa-ellipsis text-dark"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="dark">
                      <Dropdown.Item
                        id={`${componentName}-ExportCSV`}
                        disabled={
                          isEmpty(totalSize) ||
                          (totalSize > PRINT_THRESHOLD && selectedPatients.size === 0) ||
                          !filterApplied ||
                          multipleFilterApplied
                        }
                        onClick={() => exportPatients()}
                      >
                        Export to CSV
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </Col>
            </Row>
          </>
        )}
        {(nowTotalSize > 0 || soonTotalSize > 0 || priorityTotalSize > 0 || patientsList.default.length > 0 || searchTerm.trim().length >= MIN_SEARCH_LENGTH) && (
          <ListView
            updatePatients={setPatientsList}
            patientList={filteredPatientList}
            activeFilterView={activeFilterView}
            quickListType={quickListType}
            loadingData={loadingData}
            listViewLoadingData={listViewLoadingData}
            handleChangeCheckBox={selectPatient}
            handleAllPatientsCheckbox={selectPatients}
            isPatientSelected={isPatientSelected}
            quicklistId={quicklistId}
            returnToMyPatientsUrl={returnToMyPatientsUrl}
            updateParent={updateParent}
            closeEvidenceModal={closeEvidenceModal}
            reloadPatientView={reloadPatientView}
            careManagementActions={careManagementActions}
            titleProp={titleProp}
            handleFilterChange={handleFilterChange}
            filterParams={filterParams}
            pageNumber={pageNumber}
            filterApplied={filterApplied}
            selectedFilter={selectedFilterRef.current}
            workListStatusSize={[]}
          />
        )}
        {!loadingData && !listViewLoadingData && filteredPatientList.default.length === 0 && (
          <Row>
            <Col>
              <Card>
                <Card.Body>
                  <Card.Text id={`${componentName}-NoPatients`}>
                    {emptyStateMessage}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </div>
      <Modals
        show={modalMessage != null}
        onHide={() => setModalMessage(null)}
        title={modalTitle}
        size={modalSize || "md"}
        hideClickButton={true}
        showExtraButton={false}
        hideFooter={false}
        closeBtnId={`${componentName}-closeButton`}
        body={modalMessage}
      />
    </Container>
  );
};

QuickLists.propTypes = {
  titleProp: PropTypes.string.isRequired,
  filters: PropTypes.array,
  pageNumberProp: PropTypes.number,
  pageSizeProp: PropTypes.number,
  totalSizeProp: PropTypes.number,
  defaultFilterParamsProp: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  defaultSortParamsProp: PropTypes.array,
  exportPatientsUrl: PropTypes.string,
  quickListType: PropTypes.string,
  cardViewActionObj: PropTypes.object,
  listViewActionObj: PropTypes.object,
  startingView: PropTypes.string,
  careManagementActions: PropTypes.object,
};

export { QuickLists, VIEW, LAST_QUERY_SETTINGS_SUFFIX, RECENT_QUERY_SETTINGS_KEY };
